/**
 * Client-side impersonation state: which user a superadmin is acting as, and
 * whether the write override is on.
 *
 * This is a *request decoration*, not a privilege — the API re-authorizes
 * every request against the bearer token's own identity (see
 * apps/api/src/auth/identity.ts), so an ordinary user setting these values
 * gets 403s, not access. Keeping it here means nothing about impersonation
 * has to thread through component props.
 *
 * Stored in sessionStorage rather than localStorage on purpose: an
 * impersonation session should not outlive the tab, and should not silently
 * apply to every other tab you have open.
 */
import { invalidate } from "../api/invalidate.js";

const STORAGE_KEY = "mmm.impersonation";

export interface ImpersonationState {
  targetUserId: string | null;
  write: boolean;
}

const NONE: ImpersonationState = { targetUserId: null, write: false };

let state: ImpersonationState = readStored();
const listeners = new Set<() => void>();

function readStored(): ImpersonationState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return NONE;
    const parsed = JSON.parse(raw) as Partial<ImpersonationState>;
    if (typeof parsed?.targetUserId !== "string" || !parsed.targetUserId) return NONE;
    return { targetUserId: parsed.targetUserId, write: parsed.write === true };
  } catch {
    // Private-mode storage errors and malformed JSON both mean "not
    // impersonating" — never a crash on boot.
    return NONE;
  }
}

function persist(next: ImpersonationState): void {
  try {
    if (next.targetUserId) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal: the in-memory state below still drives this tab's requests.
  }
}

function commit(next: ImpersonationState): void {
  state = next;
  persist(next);
  listeners.forEach((listener) => listener());
  // Every cached read belongs to the previous identity — refetch them all
  // rather than briefly showing one user's data under another's name.
  invalidate();
}

/** Read by api/client.ts on every request. */
export function getImpersonation(): ImpersonationState {
  return state;
}

export function startImpersonation(targetUserId: string): void {
  // Always starts read-only; enabling writes is a separate, deliberate act.
  commit({ targetUserId, write: false });
}

export function stopImpersonation(): void {
  commit(NONE);
}

export function setImpersonationWrite(write: boolean): void {
  if (!state.targetUserId) return;
  commit({ ...state, write });
}

export function subscribeToImpersonation(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset so suites don't leak impersonation between cases. */
export function resetImpersonationForTests(): void {
  state = NONE;
  persist(NONE);
  listeners.forEach((listener) => listener());
}
